import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { canCreateEvent } from '../../domain/events/policy';
import { groupEventsByResourceForDate } from '../../domain/floorPlan/eventsByResource';
import { rulesForResourceOnDate } from '../../domain/guestAccess/guestAccess';
import { EventCard } from '../../features/events/EventCard';
import { useMatchingEventViews } from '../../features/matching/useMatchingEventViews';
import { useActiveGuestAccess } from '../../hooks/useActiveGuestAccess';
import { getResourceLabel, hasResource, isKnownResource } from '../../resources/resources';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchEventsDate } from '../../store/thunks';
import { formatDateHuman } from '../../utils/date';

import './ResourceEventsPage.css';

export function ResourceEventsPage() {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const events = useAppSelector((s) => s.entities.events.items);
  const role = useAppSelector((s) => s.auth.user?.role);
  const dateKey = useAppSelector((s) => s.ui.selectedDate);

  const selectedDate = React.useMemo(() => new Date(dateKey + 'T00:00:00'), [dateKey]);

  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchEventsDate({ date: dateKey })).unwrap();
      } catch {
        // handled by Redux / API client
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [dateKey, dispatch]);

  const resourceEvents = React.useMemo(() => {
    if (!resourceId) return [];
    const byResource = groupEventsByResourceForDate(events, dateKey);
    return byResource.get(resourceId) ?? [];
  }, [events, dateKey, resourceId]);
  useMatchingEventViews(React.useMemo(() => resourceEvents.map((e) => e.id), [resourceEvents]));

  const { rules: guestRules } = useActiveGuestAccess();
  const canCreate = canCreateEvent(role, rulesForResourceOnDate(guestRules, resourceId ?? '', dateKey).length > 0);
  const label = resourceId ? getResourceLabel(resourceId) : '';
  const validResource = resourceId ? isKnownResource(resourceId) : false;
  const visibleResource = resourceId ? hasResource(resourceId) : false;

  if (!resourceId || !validResource) {
    return (
      <div className="ttbPage resource-events-page">
        <div className="resource-events-page__header">
          <button className="resource-events-page__back" onClick={() => navigate('/floor-plan')}>
            ← Назад
          </button>
        </div>
        <div className="tavern-empty-state">
          <div className="tavern-empty-state-icon">🗺️</div>
          <div className="tavern-empty-state-title">Ресурс не найден</div>
        </div>
      </div>
    );
  }

  if (!visibleResource) {
    return (
      <div className="ttbPage resource-events-page">
        <div className="resource-events-page__header">
          <button className="resource-events-page__back" onClick={() => navigate('/floor-plan')}>
            ← Назад
          </button>
        </div>
        <div className="tavern-empty-state">
          <div className="tavern-empty-state-icon">⚙️</div>
          <div className="tavern-empty-state-title">{label} временно скрыт</div>
          <div className="tavern-empty-state-description">
            Для этого ресурса сейчас используется другая VIP-схема. Откройте план таверны или создайте событие без
            предвыбранного ресурса.
          </div>
          {canCreate ? (
            <button
              type="button"
              className="resource-events-page__create-btn"
              onClick={() => navigate(`/events/new?date=${encodeURIComponent(dateKey)}`)}
            >
              <span>➕</span>
              <span>Создать приключение</span>
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="ttbPage resource-events-page">
      <div className="resource-events-page__header">
        <button className="resource-events-page__back" onClick={() => navigate('/floor-plan')}>
          ← План таверны
        </button>
        <h1 className="resource-events-page__resource-name">{label}</h1>
        <div className="resource-events-page__date">
          📅 {formatDateHuman(selectedDate)}
        </div>
      </div>

      {canCreate && (
        <button
          type="button"
          className="resource-events-page__create-btn"
          onClick={() =>
            navigate(
              `/events/new?resourceId=${encodeURIComponent(resourceId)}&date=${encodeURIComponent(dateKey)}`,
            )
          }
        >
          <span>➕</span>
          <span>Создать приключение</span>
        </button>
      )}

      <div className="resource-events-page__content">
        {isLoading ? (
          <div className="tavern-loading">
            <div className="tavern-spinner" />
          </div>
        ) : resourceEvents.length === 0 ? (
          <div className="tavern-empty-state">
            <div className="tavern-empty-state-icon">📜</div>
            <div className="tavern-empty-state-title">Приключений нет</div>
            <div className="tavern-empty-state-description">
              На выбранный день мероприятий не запланировано
            </div>
          </div>
        ) : (
          <div className="resource-events-page__list">
            {resourceEvents.map((e) => (
              <EventCard key={e.id} event={e} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
